import React, { useEffect, useMemo, useState } from 'react';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {
  Loader2,
  LogOut,
  Search,
  FileText,
  Download,
  RefreshCw,
  Building2,
  UserRound,
  Mail,
  Phone,
  Plus,
  X,
  Tag,
  Send,
  CircleDot,
} from 'lucide-react';
import {
  staffingAuth,
  staffingDb,
  staffingStorage,
  staffingFunctions,
} from '../firebase';

import { Link } from 'react-router-dom';

const statusColumns = ['new', 'reviewing', 'contacted', 'closed'];
const priorityOptions = ['low', 'normal', 'high'];

function fmtDate(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString();
}

function inquiryTitle(item) {
  if (item.type === 'hospital') {
    return `${item.facility || 'Hospital Inquiry'}${item.coverageType ? ` • ${item.coverageType}` : ''}`;
  }
  if (item.type === 'clinician') {
    return `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Clinician Inquiry';
  }
  return item.subject || 'General Inquiry';
}

function secondaryLine(item) {
  if (item.type === 'hospital') {
    return [item.city, item.state].filter(Boolean).join(', ') || item.email || '—';
  }
  if (item.type === 'clinician') {
    return item.specialty || item.email || '—';
  }
  return item.email || '—';
}

function badgeClass(status) {
  switch (status) {
    case 'reviewing':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'contacted':
      return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'closed':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    default:
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
  }
}

function priorityClass(priority) {
  switch (priority) {
    case 'high':
      return 'text-red-300';
    case 'low':
      return 'text-slate-400';
    default:
      return 'text-[#c9a84c]';
  }
}

export default function StaffingDashboard() {
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [openingCv, setOpeningCv] = useState(false);
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(staffingAuth, (nextUser) => {
      setUser(nextUser);
      setCheckingAuth(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setLoadingItems(false);
      return;
    }

    setLoadingItems(true);

    const q = query(
      collection(staffingDb, 'staffingInquiries'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({
          id: d.id,
          status: 'new',
          priority: 'normal',
          tags: [],
          internalNotes: '',
          replyHistory: [],
          ...d.data(),
        }));
        setItems(next);
        setLoadingItems(false);
        if (!selectedId && next.length) {
          setSelectedId(next[0].id);
        }
      },
      (err) => {
        console.error('Dashboard snapshot error:', err);
        setLoadingItems(false);
      }
    );

    return () => unsub();
  }, [user, selectedId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchesType = filterType === 'all' || item.type === filterType;
      const matchesPriority =
        filterPriority === 'all' || (item.priority || 'normal') === filterPriority;

      const haystack = [
        item.firstName,
        item.lastName,
        item.email,
        item.phone,
        item.facility,
        item.city,
        item.state,
        item.specialty,
        item.coverageType,
        item.subject,
        item.message,
        item.notes,
        item.details,
        ...(item.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !q || haystack.includes(q);

      return matchesType && matchesPriority && matchesSearch;
    });
  }, [items, filterType, filterPriority, search]);

  const selected =
    items.find((item) => item.id === selectedId) ||
    filtered.find((item) => item.id === selectedId) ||
    filtered[0] ||
    null;

  const grouped = useMemo(() => {
    return statusColumns.reduce((acc, status) => {
      acc[status] = filtered.filter((item) => (item.status || 'new') === status);
      return acc;
    }, {});
  }, [filtered]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');

    try {
      await signInWithEmailAndPassword(
        staffingAuth,
        loginForm.email.trim(),
        loginForm.password
      );
    } catch (err) {
      console.error(err);
      setLoginError('Unable to sign in. Check your email and password.');
    } finally {
      setLoggingIn(false);
    }
  };

  const patchInquiry = async (id, patch) => {
    setSaving(true);
    try {
      await updateDoc(doc(staffingDb, 'staffingInquiries', id), {
        ...patch,
        updatedAt: new Date(),
      });
    } catch (err) {
      console.error('Dashboard update error:', err);
      alert('Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCv = async (cvPath) => {
    if (!cvPath) return;
    setOpeningCv(true);
    try {
      const url = await getDownloadURL(ref(staffingStorage, cvPath));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('CV open error:', err);
      alert('Could not open CV.');
    } finally {
      setOpeningCv(false);
    }
  };

  const moveCardToStatus = async (id, nextStatus) => {
    await patchInquiry(id, { status: nextStatus });
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#040d1c] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-300">
          <Loader2 className="animate-spin" size={18} />
          Loading dashboard…
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#040d1c] text-white flex items-center justify-center px-6">
        <div className="w-full max-w-md rounded-3xl border border-[#1e2d4a] bg-[#0b1628] p-8 shadow-2xl shadow-black/50">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#c9a84c] mb-2">
            Lumosye Staffing
          </p>
          <p className="text-slate-400 text-sm mb-6">
  Use a staffing admin account to access the dashboard.
</p>
<Link
  to="/medical-staffing"
  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#c9a84c] transition mb-2"
>
  ← Back to Medical Staffing
</Link>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
              value={loginForm.email}
              onChange={(e) =>
                setLoginForm((f) => ({ ...f, email: e.target.value }))
              }
              required
            />

            <input
              type="password"
              placeholder="Password"
              className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
              value={loginForm.password}
              onChange={(e) =>
                setLoginForm((f) => ({ ...f, password: e.target.value }))
              }
              required
            />

            {loginError && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loggingIn}
              className="w-full rounded-full bg-[#c9a84c] py-3 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] disabled:opacity-60"
            >
              {loggingIn ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#040d1c] text-white">
      <header className="sticky top-0 z-30 border-b border-[#1e2d4a] bg-[#040d1c]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#c9a84c]">
              Lumosye Staffing
            </p>
            <h1 className="text-2xl font-black">ATS Dashboard</h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm text-white">{user.email}</p>
              <p className="text-xs text-slate-500">Signed in</p>
            </div>
            <button
              onClick={() => signOut(staffingAuth)}
              className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-4 py-2 text-sm text-slate-300 hover:border-[#c9a84c]/40 hover:text-white"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-6 py-6 space-y-6">
        <div className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628] p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px_180px] gap-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                placeholder="Search name, email, facility, specialty, tags..."
                className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] pl-10 pr-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select
              className="rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#c9a84c]/60"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All types</option>
              <option value="hospital">Hospital</option>
              <option value="clinician">Clinician</option>
              <option value="general">General</option>
            </select>

            <select
              className="rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#c9a84c]/60"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="all">All priority</option>
              {priorityOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_520px] gap-6">
          <div className="overflow-x-auto">
            <div className="grid grid-cols-4 gap-4 min-w-[1100px]">
              {statusColumns.map((status) => (
                <div
                  key={status}
                  className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628] min-h-[72vh]"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/plain');
                    setDraggingId(null);
                    if (id) await moveCardToStatus(id, status);
                  }}
                >
                  <div className="px-4 py-4 border-b border-[#1e2d4a] flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-widest text-[#c9a84c]">
                        {status}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {grouped[status]?.length || 0} item(s)
                      </p>
                    </div>
                  </div>

                  <div className="p-3 space-y-3">
                    {loadingItems ? (
                      <div className="p-4 text-slate-500">Loading…</div>
                    ) : grouped[status]?.length ? (
                      grouped[status].map((item) => (
                        <button
                          key={item.id}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', item.id);
                            setDraggingId(item.id);
                          }}
                          onDragEnd={() => setDraggingId(null)}
                          onClick={() => setSelectedId(item.id)}
                          className={`w-full text-left rounded-2xl border p-4 transition ${
                            selected?.id === item.id
                              ? 'border-[#c9a84c]/50 bg-[#101d33]'
                              : 'border-[#1e2d4a] bg-[#071325] hover:border-[#c9a84c]/30'
                          } ${
                            draggingId === item.id ? 'opacity-60' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-white truncate">
                                {inquiryTitle(item)}
                              </p>
                              <p className="text-xs text-slate-500 mt-1 truncate">
                                {secondaryLine(item)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <CircleDot
                                size={14}
                                className={priorityClass(item.priority || 'normal')}
                              />
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap mb-3">
                            <span
                              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase ${badgeClass(
                                item.status || 'new'
                              )}`}
                            >
                              {item.status || 'new'}
                            </span>
                            <span className="text-[11px] uppercase tracking-wider text-slate-500">
                              {item.type}
                            </span>
                          </div>

                          {!!item.tags?.length && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {item.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/20 px-2.5 py-1 text-[11px] text-[#c9a84c]"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>{fmtDate(item.createdAt)}</span>
                            <div className="flex items-center gap-2">
                              {item.email && (
                                <a
                                  href={`mailto:${item.email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hover:text-white"
                                  title="Email"
                                >
                                  <Mail size={13} />
                                </a>
                              )}
                              {item.phone && (
                                <a
                                  href={`tel:${item.phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hover:text-white"
                                  title="Call"
                                >
                                  <Phone size={13} />
                                </a>
                              )}
                              {item.cvPath && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenCv(item.cvPath);
                                  }}
                                  className="hover:text-white"
                                  title="Open CV"
                                >
                                  <Download size={13} />
                                </button>
                              )}
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-sm text-slate-500">No items</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628] p-6">
            {!selected ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                Select an inquiry
              </div>
            ) : (
              <DashboardDetail
                item={selected}
                saving={saving}
                openingCv={openingCv}
                patchInquiry={patchInquiry}
                onOpenCv={handleOpenCv}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DashboardDetail({ item, saving, openingCv, patchInquiry, onOpenCv }) {
  const [notesDraft, setNotesDraft] = useState(item.internalNotes || '');
  const [tagInput, setTagInput] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    setNotesDraft(item.internalNotes || '');
    setReplySubject(
      item.type === 'hospital'
        ? `Re: ${item.facility || 'Hospital Coverage Request'}`
        : item.type === 'clinician'
        ? `Re: Your Lumosye Staffing Inquiry`
        : `Re: ${item.subject || 'Your Inquiry'}`
    );
    setReplyMessage('');
  }, [item.id, item.internalNotes, item.type, item.facility, item.subject]);

  const addTag = async () => {
    const next = tagInput.trim();
    if (!next) return;
    const tags = Array.from(new Set([...(item.tags || []), next]));
    await patchInquiry(item.id, { tags });
    setTagInput('');
  };

  const removeTag = async (tag) => {
    const tags = (item.tags || []).filter((t) => t !== tag);
    await patchInquiry(item.id, { tags });
  };

  const sendReply = async () => {
    if (!replySubject.trim() || !replyMessage.trim()) {
      alert('Subject and message are required.');
      return;
    }

    setSendingReply(true);
    try {
      const fn = httpsCallable(staffingFunctions, 'sendStaffingReply');
      await fn({
        inquiryId: item.id,
        subject: replySubject.trim(),
        message: replyMessage.trim(),
      });
      setReplyMessage('');
      alert('Reply sent.');
    } catch (err) {
      console.error('Reply send error:', err);
      alert('Could not send reply.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#c9a84c] mb-2">
            {item.type}
          </p>
          <h2 className="text-2xl font-black text-white">{inquiryTitle(item)}</h2>
          <p className="text-sm text-slate-500 mt-2">
            Submitted {fmtDate(item.createdAt)}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <select
            className="rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#c9a84c]/60"
            value={item.status || 'new'}
            onChange={(e) => patchInquiry(item.id, { status: e.target.value })}
            disabled={saving}
          >
            {statusColumns.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            className="rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#c9a84c]/60"
            value={item.priority || 'normal'}
            onChange={(e) => patchInquiry(item.id, { priority: e.target.value })}
            disabled={saving}
          >
            {priorityOptions.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          {item.cvPath && (
            <button
              onClick={() => onOpenCv(item.cvPath)}
              disabled={openingCv}
              className="inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-4 py-2.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] disabled:opacity-60"
            >
              {openingCv ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              CV
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <InfoCard
          icon={<UserRound size={16} />}
          title="Contact"
          rows={[
            item.firstName || item.lastName
              ? `${item.firstName || ''} ${item.lastName || ''}`.trim()
              : null,
            item.email,
            item.phone,
          ]}
        />
        <InfoCard
          icon={<Building2 size={16} />}
          title="Details"
          rows={[
            item.facility,
            item.coverageType,
            item.specialty,
            item.credential,
            item.licensedStates,
            item.city && item.state ? `${item.city}, ${item.state}` : item.city || item.state,
          ]}
        />
      </div>

      <LongCard title="Quick Actions">
        <div className="flex flex-wrap gap-3">
          {item.email && (
            <a
              href={`mailto:${item.email}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-4 py-2.5 text-sm text-slate-300 hover:border-[#c9a84c]/40 hover:text-white"
            >
              <Mail size={14} />
              Open Email App
            </a>
          )}

          {item.phone && (
            <a
              href={`tel:${item.phone}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-4 py-2.5 text-sm text-slate-300 hover:border-[#c9a84c]/40 hover:text-white"
            >
              <Phone size={14} />
              Call
            </a>
          )}

          <button
            onClick={() => patchInquiry(item.id, { status: 'contacted' })}
            className="inline-flex items-center gap-2 rounded-full border border-[#1e2d4a] px-4 py-2.5 text-sm text-slate-300 hover:border-[#c9a84c]/40 hover:text-white"
          >
            <CircleDot size={14} />
            Mark Contacted
          </button>
        </div>
      </LongCard>

      <LongCard title="Tags">
        <div className="flex flex-wrap gap-2 mb-3">
          {(item.tags || []).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-2 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/20 px-3 py-1.5 text-xs text-[#c9a84c]"
            >
              <Tag size={12} />
              {tag}
              <button onClick={() => removeTag(tag)}>
                <X size={12} />
              </button>
            </span>
          ))}
          {!item.tags?.length && (
            <span className="text-sm text-slate-500">No tags yet.</span>
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Add tag"
            className="flex-1 rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
          />
          <button
            onClick={addTag}
            className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-3 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c]"
          >
            <Plus size={14} />
            Add
          </button>
        </div>
      </LongCard>

      <div className="grid md:grid-cols-2 gap-4">
        <LongCard title="Message / Notes">
          <div className="space-y-3 text-sm text-slate-300">
            {item.message && <p>{item.message}</p>}
            {item.details && <p><span className="text-slate-500">Details:</span> {item.details}</p>}
            {item.notes && <p><span className="text-slate-500">Notes:</span> {item.notes}</p>}
            {item.subject && <p><span className="text-slate-500">Subject:</span> {item.subject}</p>}
            {item.assignmentPref && (
              <p><span className="text-slate-500">Assignment preference:</span> {item.assignmentPref}</p>
            )}
            {item.availability && (
              <p><span className="text-slate-500">Availability:</span> {item.availability}</p>
            )}
          </div>
        </LongCard>

        <LongCard title="Internal Tracking">
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Completion time: {item.fillDurationMs ? `${Math.round(item.fillDurationMs / 1000)}s` : '—'}
            </div>
            <textarea
              rows={8}
              className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
              placeholder="Add internal notes..."
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
            />
            <button
              onClick={() => patchInquiry(item.id, { internalNotes: notesDraft })}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 px-4 py-2.5 text-sm font-semibold text-[#c9a84c] hover:bg-[#c9a84c]/20 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              Save notes
            </button>
          </div>
        </LongCard>
      </div>

      <LongCard title="Reply from Dashboard">
        <div className="space-y-3">
          <input
            type="text"
            className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
            value={replySubject}
            onChange={(e) => setReplySubject(e.target.value)}
            placeholder="Reply subject"
          />
          <textarea
            rows={6}
            className="w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            placeholder="Write your message..."
          />
          <button
            onClick={sendReply}
            disabled={sendingReply}
            className="inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-4 py-2.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] disabled:opacity-60"
          >
            {sendingReply ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send Reply
          </button>
        </div>
      </LongCard>

      <LongCard title="Activity">
        <div className="space-y-3">
          {item.replyHistory?.length ? (
            item.replyHistory
              .slice()
              .reverse()
              .map((entry, idx) => (
                <div
                  key={`${entry.sentAt || idx}-${idx}`}
                  className="rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3"
                >
                  <p className="text-sm text-white font-semibold">{entry.subject}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {entry.by} • {fmtDate(entry.sentAt)}
                  </p>
                  {entry.preview && (
                    <p className="text-sm text-slate-300 mt-2">{entry.preview}</p>
                  )}
                </div>
              ))
          ) : (
            <p className="text-sm text-slate-500">No activity yet.</p>
          )}
        </div>
      </LongCard>
    </div>
  );
}

function InfoCard({ icon, title, rows }) {
  const cleanRows = rows.filter(Boolean);

  return (
    <div className="rounded-2xl border border-[#1e2d4a] bg-[#071325] p-5">
      <div className="flex items-center gap-2 text-[#c9a84c] mb-3">
        {icon}
        <h3 className="text-sm font-bold uppercase tracking-widest">{title}</h3>
      </div>
      <div className="space-y-2 text-sm text-slate-300">
        {cleanRows.length ? cleanRows.map((row) => <p key={row}>{row}</p>) : <p className="text-slate-500">—</p>}
      </div>
    </div>
  );
}

function LongCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-[#1e2d4a] bg-[#071325] p-5">
      <h3 className="text-sm font-bold uppercase tracking-widest text-[#c9a84c] mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}